import { useEffect, useRef, useState } from 'react';
import classes from './SingleMultiQuestion.module.scss';
import { Dialog, sizeCalc, useGameSounds } from '@educaplay/core';
import { GAMEMODE, QUESTION_TYPE, VIDEOQUIZ_SOUNDS, VIEW_COUNT_DURATION } from '../../../../utils/constants';
import { giveAnswerStyles } from '../../../../utils/functions';
import { motion } from 'framer-motion';
import PillMessage from './PillMessage';
import useSlice from '../../../../hooks/useSlice';
import useVideoQuizConfig from '../../../../hooks/useVideoQuizConfig';
import { CheckButton } from './CheckButton';
import IndividualQuestion from './IndividualQuestion';
import useVideo from '../../../../hooks/useVideo';
const ANSWER_DELAY = 500;
const INTERVAL_SOUND_DELAY = 100;
const formVariants = {
    hidden: {
    },
    show: {
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.6,
            delay: 0.6
        }
    }
}

export function SingleMultiQuestion(props) {

    const { answers, typeOfQuestion, correctAnswers, onAnswer, previousAnswers } = props;
    const { progress } = useVideo();
    const { gameMode, sizeLetterQuestion } = useVideoQuizConfig();
    const { markQuestionViewed, showSendButton, currentQuestionTimeAppear } = useSlice();
    const sizeClass = sizeCalc(sizeLetterQuestion);
    const [numChecked, setNumChecked] = useState(0);
    const showButton = typeOfQuestion === QUESTION_TYPE.MultiQuestion && numChecked > 0;
    const [selectedOption, setSelectedOption] = useState(previousAnswers != null ? previousAnswers : typeOfQuestion === QUESTION_TYPE.SingleQuestion ? null : []);
    const [dialog, setDialog] = useState({ view: false, source: null });
    const [answersFeedback, setAnswersFeedback] = useState(null);
    const [answeredQuestion, setAnsweredQuestion] = useState(previousAnswers != null);
    const buttonRef = useRef(null);
    const canPassQuestion = answeredQuestion && previousAnswers == null;
    const gameSound = useGameSounds();
    const [animationComplete, setAnimationComplete] = useState(false);
    
    useEffect(() => {
        const intervalID = setInterval(() => {
            setTimeout(() => {
                gameSound.play(VIDEOQUIZ_SOUNDS.CHARGE);
            }, 600);
        }, INTERVAL_SOUND_DELAY);

        setTimeout(() => {
            clearInterval(intervalID);
        }, INTERVAL_SOUND_DELAY * answers.length);

        return () => {
            clearInterval(intervalID);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (progress !== currentQuestionTimeAppear || previousAnswers === null) return;
        const timeId = setTimeout(() => {
            markQuestionViewed();
        }, gameMode === GAMEMODE.FREEDOM ? VIEW_COUNT_DURATION : 0)

        return () => {
            clearTimeout(timeId)
        }
    }, [markQuestionViewed, gameMode, progress, currentQuestionTimeAppear, previousAnswers])

    const onChangeHandler = (ev) => {
        if (dialog.open || answeredQuestion) return;

        if (typeOfQuestion === QUESTION_TYPE.SingleQuestion) {
            setAnsweredQuestion(true);

            const answerSelected = Number(ev.target.value);

            setSelectedOption(answerSelected);
            gameSound.play(VIDEOQUIZ_SOUNDS.SUBMIT);

            const answerStyles = giveAnswerStyles(answers, correctAnswers, answerSelected, typeOfQuestion);

            setTimeout(() => {
                setAnswersFeedback(answerStyles);
            }, (ANSWER_DELAY));

            setTimeout(() => {
                onAnswer(answerSelected)
            }, ANSWER_DELAY - 200);

            return;
        }

        const { value: id, checked } = ev.target;

        let newSelectedOptions;
        if (checked) {
            newSelectedOptions = [...selectedOption, Number(id)];
            gameSound.play(VIDEOQUIZ_SOUNDS.SUBMIT);
        } else {
            newSelectedOptions = selectedOption.filter(option => option !== Number(id));
        }
        setSelectedOption(newSelectedOptions);
        setNumChecked(newSelectedOptions.length);
        if (newSelectedOptions.length > 0) setTimeout(() => buttonRef.current?.focus(), 0);
    }

    const answersFiltered = answers.map((answer) => {
        return {
            ...answer,
            className: answersFeedback?.[answer.id] ?? ''
        };
    });
    const onSubmitHandler = (ev) => {
        ev.preventDefault();
        gameSound.play(VIDEOQUIZ_SOUNDS.SUBMIT);
        if (answeredQuestion) return;
        setAnsweredQuestion(true);
        const answerStyles = giveAnswerStyles(answers, correctAnswers, selectedOption, typeOfQuestion);

        setTimeout(() => {
            setAnswersFeedback(answerStyles);
        }, (ANSWER_DELAY + 250));

        setTimeout(() => {
            onAnswer(selectedOption)
        }, ANSWER_DELAY);

    }

    useEffect(() => {
        if (previousAnswers == null) return;
        const answerStyles = giveAnswerStyles(answers, correctAnswers, previousAnswers, typeOfQuestion);
        setAnswersFeedback(answerStyles);
    }, [previousAnswers, answers, correctAnswers, typeOfQuestion])

    useEffect(() => {
        if (!showButton) return;
        gameSound.play(VIDEOQUIZ_SOUNDS.MOVE);
    }, [showButton, gameSound])

    return (
        <>
            <Dialog open={dialog.open ?? false}
                onClose={(e) => {
                    e.stopPropagation();
                    setDialog({ open: false, source: null });
                }}
                variant="lightbox">
                <img src={dialog.source} alt="" />
            </Dialog>
            <PillMessage open={typeOfQuestion === QUESTION_TYPE.MultiQuestion && previousAnswers == null} />
            <motion.form
                initial="hidden"
                animate="show"
                onAnimationComplete={() => setAnimationComplete(true)}
                variants={formVariants}
                className={classes.formContainer}
                onSubmit={onSubmitHandler}
                onClick={(e) => e.stopPropagation()}
                style={{ pointerEvents: canPassQuestion ? 'auto' : 'none' }}
            >
                <div className={classes.questionsContainer}>
                    {answersFiltered.map((answer, index) => {
                        const isSelected = typeOfQuestion === QUESTION_TYPE.SingleQuestion ? selectedOption === answer.id : selectedOption && selectedOption.includes(answer.id);
                        return (
                            <IndividualQuestion
                                key={`answer_${index}`}
                                index={index}
                                answer={answer}
                                isSelected={isSelected}
                                type={typeOfQuestion}
                                previousAnswers={previousAnswers}
                                animationComplete={animationComplete}
                                onChangeHandler={onChangeHandler}
                                onClickImageHandler={() => setDialog({ open: true, source: answer.source })}
                                sizeClass={sizeClass}
                            />
                        );
                    })}
                </div>
                <CheckButton show={showButton} answeredQuestion={answeredQuestion} showSendButton={showSendButton} ref={buttonRef} />
            </motion.form>

        </>
    )
}